<template>
  <div class="customer-material-name">
    <!-- 사내통용 원료명칭 검색 모달 -->
    <v-dialog v-model="searchModal" max-width="900px" max-height="700px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">사내통용 원료명칭 검색</span>
        </v-card-title>
        <v-card-text>
          <!-- 검색 영역 -->
          <v-form @submit.prevent="searchInternalMaterials">
            <div class="search-area mb-4">
              <v-row align="center">
                <v-col cols="3">
                  <span class="search-label">사내통용 원료</span>
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="searchKeyword" outlined dense placeholder="검색어를 입력하세요" @keyup.enter="searchInternalMaterials"></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-btn depressed color="green darken-1" class="white--text" @click="searchInternalMaterials"> 검색 </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-form>

          <!-- 그리드 영역 -->
          <div class="grid-area" style="height: 350px; overflow: hidden">
            <RealGrid :domName="searchGridName" ref="searchGrid" :settings="searchGridSettings" @changePage="handleSearchPageChange" @click="handleSearchGridClick" @checkChange="handleSearchGridCheckChange" />
          </div>

          <!-- 검색 모달 페이징 -->
          <div class="pagination-container" v-if="searchPagination.totalRows > 0">
            <v-pagination :value="searchPagination.currentPage" :length="Math.ceil(searchPagination.totalRows / searchPagination.pageSize)" :total-visible="7" @input="handleSearchPageChange" circle></v-pagination>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeSearchModal">취소</v-btn>
          <v-btn color="green darken-1" text @click="selectMaterial" :disabled="!hasSelectedMaterial">확인</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <SetPopup ref="confirm" />
    <ValidationAlert ref="validationAlert" />

    <v-form ref="customerMaterialName" lazy-validation>
      <div class="filter-box filter-box--row">
        <ul class="filter-list_free">
          <li>
            <h4>고객사 원료명칭</h4>
            <v-text-field outlined dense placeholder="" v-model="param.customerMaterialName" @keyup.enter="search"></v-text-field>
          </li>
          <li>
            <h4>사내통용 원료명칭</h4>
            <v-text-field outlined dense placeholder="" v-model="param.internalMaterialName" @keyup.enter="search"></v-text-field>
          </li>
        </ul>
        <div class="btn-box">
          <v-card-actions>
            <v-btn depressed class="btn-reset" @click="reset">초기화</v-btn>
          </v-card-actions>
          <v-card-actions>
            <v-btn depressed class="btn-search" @click="search">검색</v-btn>
          </v-card-actions>
        </div>
      </div>
    </v-form>

    <h3 class="mt-2 mb-2 dp--row">
      <div style="margin-left: auto; display: flex; gap: 8px">
        <v-btn depressed class="btn-add" @click="addNewRow">추가</v-btn>
        <v-btn depressed class="btn-delete" @click="deleteSelectedRow">삭제</v-btn>
        <v-btn depressed class="btn-save" @click="saveData">저장</v-btn>
      </div>
    </h3>

    <RealGrid :domName="grid" ref="grid" :settings="settings" @changePage="search" @click="handleCellClick" />

    <!-- 메인 그리드 페이징 -->
    <div class="pagination-container" v-if="pagination.totalRows > 0">
      <v-pagination :value="pagination.currentPage" :length="Math.ceil(pagination.totalRows / pagination.pageSize)" :total-visible="7" @input="handleMainPageChange" circle></v-pagination>
    </div>
  </div>
</template>

<script>
import { columns, fields, hideCheckBar } from '@/assets/grid/customerMaterialName';
import { columns as searchColumns, fields as searchFields } from '@/assets/grid/internalMaterialNameSearch';
import RealGrid from '@/components/RealGrid.vue';
import SetPopup from '@/components/SetPopup.vue';
import ValidationAlert from '@/components/ValidationAlert.vue';
import { mapState, mapMutations } from 'vuex';
import { customerMaterialNameList, customerMaterialNameInsert, customerMaterialNameUpdate, customerMaterialNameDelete, searchInternalMaterialNames } from 'api/customerMaterialName/customerMaterialName';
import _ from 'lodash';

export default {
  data() {
    return {
      isFetching: false,
      isDataLoaded: false,
      grid: 'customerMaterialName',
      settings: {
        columns: _.map(_.cloneDeep(columns), function (v) {
          // ID 컬럼은 편집 불가, 나머지는 편집 가능
          if (v.fieldName !== 'id' && v.fieldName !== 'regDate' && v.fieldName !== 'regUser') {
            v.editable = true;
          }
          return v;
        }),
        fields,
        height: 666,
        errorMessage: '고객사 원료명칭 데이터를 검색해주세요',
        fixCol: 1,
        hideCheckBar: hideCheckBar, // 체크박스 활성화
        radio: true, // 체크박스 단일 선택 (exclusive 모드)
        // 편집 관련 설정은 mounted에서 직접 설정
        displayOptions: {
          fitStyle: 'fill',
          rowResizable: true,
          columnResizable: true,
        },
      },
      param: {
        customerMaterialName: '',
        internalMaterialName: '',
        pageSize: 10000,
        currentPage: 1,
      },
      currentPage: 1,
      gridData: [],
      // 메인 그리드 페이징 관련 데이터
      pagination: {
        currentPage: 1,
        pageSize: 10000,
        totalRows: 0,
      },
      // 모달 관련 상태
      searchModal: false,
      selectedRowIndex: null,
      // 검색 모달 관련 데이터
      searchKeyword: '',
      selectedMaterial: null,
      selectedMaterialData: null, // 선택된 전체 데이터
      isItemSelected: false, // 선택 상태 추적
      searchGridName: 'internalMaterialSearch',
      searchGridSettings: {
        columns: searchColumns,
        fields: searchFields,
        height: 350,
        errorMessage: '검색된 사내통용 원료명칭이 없습니다',
        fixCol: 0, // fixCol 제거
        hideCheckBar: false, // 체크박스 표시
        radio: true, // 단일 선택
        fitStyle: 'fill', // 그리드 너비에 맞춰 컬럼 조정
        checkBar: {
          exclusive: true, // 단일 선택 강제
          showAll: false, // 전체 선택 체크박스 숨김
        },
      },
      searchGridData: [],
      // 검색 모달 페이징 관련 데이터
      searchPagination: {
        currentPage: 1,
        pageSize: 10,
        totalRows: 0,
      },
    };
  },
  computed: {
    ...mapState('common', ['code']),
    ...mapState('member', ['userInfo']),

    // 선택된 원료명칭이 있는지 확인
    hasSelectedMaterial() {
      return this.isItemSelected && (this.selectedMaterial !== null || this.selectedMaterialData !== null);
    },
  },
  components: {
    RealGrid,
    SetPopup,
    ValidationAlert,
  },
  mounted() {
    this.loadData();

    // 글로벌 함수 등록 (HTML 렌더링된 셀에서 호출용)
    window.openSearchModal = (fieldName, rowIndex) => {
      // 해당 행의 데이터 확인
      const allData = this.$refs.grid.getJsonRows();
      const rowData = allData[rowIndex];

      // ID가 있는 행은 모달 열기 금지
      if (rowData && rowData.id != null && rowData.id !== '') {
        this.openPopup('기존 데이터는 수정할 수 없습니다.');
        return;
      }

      this.selectedRowIndex = rowIndex;
      this.searchModal = true;

      // 선택 상태 초기화
      this.isItemSelected = false;
      this.selectedMaterial = null;
      this.selectedMaterialData = null;

      // 모달이 열릴 때 초기 검색 실행
      this.$nextTick(() => {
        this.searchInternalMaterials();
      });
    };

    // 초기 로드 시에도 편집 제한 설정 시도
    this.$nextTick(() => {
      this.setupGridEditRestrictions();
    });
  },

  beforeDestroy() {
    if (window.openSearchModal) {
      delete window.openSearchModal;
    }
  },
  methods: {
    ...mapMutations('modal', ['SET_MODAL']),
    ...mapMutations('popup', ['SET_POPUP']),

    newSample() {
      this.$emit('newSample');
    },

    loadData(v) {
      this.search(v);
    },

    async search(v) {
      if (this.isFetching) return;
      this.isFetching = true;

      try {
        if (_.isNumber(v)) {
          this.currentPage = v;
          this.param.currentPage = v;
        }

        const res = await customerMaterialNameList({ ...this.param });
        const response = res.data;
        const items = response.data.items || [];
        const page = response.data.params || {};

        // 페이징 정보 업데이트
        this.pagination.currentPage = page.currentPage || 1;
        this.pagination.totalRows = page.totalRows || 0;

        this.isDataLoaded = true;
        this.gridData = items;
        this.$refs.grid.loadData(items);
        this.$refs.grid.setPage(page);

        // 데이터 로드 후 편집 제한 설정
        this.$nextTick(() => {
          this.setupGridEditRestrictions();
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('고객사 원료명칭 목록 조회 중 오류 발생:', err);
        this.openPopup('데이터 조회에 실패했습니다.');
      } finally {
        this.isFetching = false;
      }
    },

    // 메인 그리드 페이지 변경 핸들러
    handleMainPageChange(page) {
      this.pagination.currentPage = page;
      this.param.currentPage = page;
      this.search();
    },

    reset() {
      this.param = {
        customerMaterialName: '',
        internalMaterialName: '',
        pageSize: 10000,
        currentPage: 1,
      };
      this.currentPage = 1;
      // 페이징 정보 초기화
      this.pagination.currentPage = 1;
      this.pagination.totalRows = 0;
      this.search();
    },

    // 새 로우 추가
    addNewRow() {
      const newRow = {
        id: null, // 새 데이터는 ID가 null
        customerMaterialName: '',
        internalMaterialName: '',
        remark: '',
        regDate: new Date().toISOString().slice(0, 10),
        regUser: this.userInfo?.memberId || '',
      };

      this.$refs.grid.addRow(newRow);
    },

    // 선택된 로우 삭제
    async deleteSelectedRow() {
      const selectedRows = this.$refs.grid.gv.getCheckedRows(true, false, false);

      if (selectedRows.length === 0) {
        this.openPopup('삭제할 항목을 선택해주세요.');
        return;
      }

      // 확인 팝업
      this.openPopup('선택한 항목을 삭제하시겠습니까?', true, async () => {
        try {
          const selectedRow = this.$refs.grid.dp.getJsonRow(selectedRows[0], false);
          // 백엔드에 삭제 요청 (ID가 있는 기존 데이터인 경우만)
          if (selectedRow.id != null && selectedRow.id !== '') {
            await customerMaterialNameDelete(selectedRow.id);
          }

          // 그리드 데이터에서 삭제
          this.gridData.splice(selectedRows[0], 1);

          // 그리드 새로고침
          this.$refs.grid.loadData(this.gridData);
          this.openPopup('삭제되었습니다.');
        } catch (err) {
          console.error('삭제 중 오류 발생:', err);
          this.openPopup('삭제에 실패했습니다.');
        }
      });
    },

    // 그리드 데이터 저장 (선택된 행만 처리)
    async saveData() {
      try {
        // 선택된 행만 가져오기
        const selectedRows = this.$refs.grid.gv.getCheckedRows(true, false, false);

        if (selectedRows.length === 0) {
          this.openPopup('저장할 항목을 선택해주세요.');
          return;
        }

        const allData = this.$refs.grid.getJsonRows();

        // 선택된 행만 처리
        for (const rowIndex of selectedRows) {
          const row = allData[rowIndex];

          if (row.id == null || row.id === '') {
            // 새 데이터 등록
            const insertData = {
              customerMaterialName: row.customerMaterialName,
              internalMaterialName: row.internalMaterialName,
              remark: row.remark,
            };
            await customerMaterialNameInsert(insertData);
          } else {
            // 선택된 기존 데이터 수정
            const updateData = {
              id: row.id,
              customerMaterialName: row.customerMaterialName,
              internalMaterialName: row.internalMaterialName,
              remark: row.remark,
            };
            await customerMaterialNameUpdate(updateData);
          }
        }

        // 저장 후 목록 새로고침
        this.search();
        this.openPopup('저장되었습니다.');
      } catch (err) {
        console.error('저장 중 오류 발생:', err);

        let errorMessage = '저장에 실패했습니다.';
        if (err.response && err.response.data) {
          if (err.response.data.data) {
            // data 필드에 구체적인 메시지가 있는 경우 (예: "이미 존재하는 고객사 원료명칭입니다.")
            errorMessage = err.response.data.data;
          } else if (err.response.data.errorMessage) {
            // errorMessage 필드 사용
            errorMessage = err.response.data.errorMessage;
          }
        }

        this.openPopup(errorMessage);
      }
    },

    showValidationError(errors) {
      // 커스텀 ValidationAlert 컴포넌트 사용
      this.$refs.validationAlert.show(errors);
    },

    openPopup(text, closable = false, cb) {
      this.SET_POPUP({
        title: '알림',
        width: 300,
        closable: closable,
        text: text,
      });
      this.$refs.confirm.openPopup(cb);
    },

    // 셀 클릭 이벤트 처리
    handleCellClick(event) {
      const { grid, clickData } = event;
      const column = clickData.column;
      const itemIndex = clickData.dataRow || clickData.itemIndex;

      // 사내통용 원료명칭 컬럼 클릭 시 모달 열기 (name 또는 fieldName으로 체크)
      if (column && (column === 'internalMaterialName' || column === 'internalMaterialName1')) {
        // HTML 렌더러에서 처리하므로 여기서는 별도 동작 없음
        return;
      }
    },

    // 검색 모달 닫기
    closeSearchModal() {
      this.searchModal = false;
      this.selectedRowIndex = null;
      this.searchKeyword = '';
      this.selectedMaterial = null;
      this.selectedMaterialData = null;
      this.isItemSelected = false;
      this.searchGridData = [];
      // 검색 페이징 정보 초기화
      this.searchPagination.currentPage = 1;
      this.searchPagination.totalRows = 0;
    },

    // 사내통용 원료명칭 검색
    async searchInternalMaterials(page = 1) {
      try {
        // page가 객체인 경우 숫자로 변환
        const pageNumber = typeof page === 'object' ? 1 : page;

        const params = {
          searchKeyword: this.searchKeyword,
          currentPage: pageNumber,
          pageSize: this.searchPagination.pageSize,
        };

        const response = await searchInternalMaterialNames(params);
        const result = response.data.data;

        // 검색 페이징 정보 업데이트
        this.searchPagination.currentPage = pageNumber;
        this.searchPagination.totalRows = (result.params && result.params.totalRows) || 0;

        this.searchGridData = result.items || [];
        this.$refs.searchGrid.loadData(this.searchGridData);
        this.$refs.searchGrid.setPage(result.params || {});

        // 검색 후 선택 상태 초기화
        this.isItemSelected = false;
        this.selectedMaterial = null;
        this.selectedMaterialData = null;
      } catch (error) {
        console.error('사내통용 원료명칭 검색 중 오류:', error);
        this.openPopup('검색 중 오류가 발생했습니다.');
      }
    },

    // 검색 그리드 페이지 변경 처리
    handleSearchPageChange(page) {
      // page가 이벤트 객체인 경우 처리
      const pageNumber = typeof page === 'object' ? page.currentPage || 1 : page;
      this.searchInternalMaterials(pageNumber);
    },

    // 검색 그리드 클릭 이벤트 처리
    handleSearchGridClick(event) {
      const { grid, clickData } = event;
      const itemIndex = clickData.dataRow || clickData.itemIndex;

      if (itemIndex >= 0) {
        const selectedData = grid.getValue(itemIndex, 'internalMaterialName');
        this.selectedMaterial = selectedData;

        // 클릭 시에도 선택 상태 업데이트
        setTimeout(() => {
          this.updateSelectionState();
        }, 100);
      }
    },

    // 검색 그리드 체크박스 변경 이벤트 처리
    handleSearchGridCheckChange(event) {
      this.updateSelectionState();
    },

    // 선택 상태 업데이트
    updateSelectionState() {
      this.$nextTick(() => {
        try {
          if (this.$refs.searchGrid) {
            const checkedRows = this.$refs.searchGrid.getCheckedRow();

            if (checkedRows && checkedRows.length > 0) {
              this.isItemSelected = true;
              this.selectedMaterialData = checkedRows[0].data;
              this.selectedMaterial = this.selectedMaterialData.internalMaterialName;
            } else {
              this.isItemSelected = false;
              this.selectedMaterialData = null;
              this.selectedMaterial = null;
            }
          }
        } catch (error) {
          this.isItemSelected = false;
        }
      });
    },

    // 편집 제한: ID가 있는 기존 행은 편집 불가
    setupGridEditRestrictions() {
      if (this.$refs.grid && this.$refs.grid.gv) {
        this.$refs.grid.gv.setRowStyleCallback(function (grid, item, fixed) {
          var ret = {};
          var id = grid.getValue(item.index, 'id');
          if (id != null && id !== '') {
            ret.editable = false;
          }
          return ret;
        });
      }
    },

    // 원료명칭 선택 확인
    selectMaterial() {
      if (this.isItemSelected && this.selectedMaterial && this.selectedRowIndex !== null) {
        // 선택된 원료명칭을 원래 그리드의 해당 행에 설정
        const allData = this.$refs.grid.getJsonRows();
        if (allData[this.selectedRowIndex]) {
          allData[this.selectedRowIndex].internalMaterialName = this.selectedMaterial;
          this.$refs.grid.loadData(allData);
          this.gridData = allData;
        }

        this.closeSearchModal();
      } else {
        this.openPopup('원료명칭을 선택해주세요.');
      }
    },
  },
};
</script>

<style lang="scss">
.customer-material-name {
  .v-input__slot {
    width: 100% !important;
  }

  // 검색 모달 스타일
  .search-area {
    .search-label {
      font-weight: 500;
      color: #333;
      line-height: 40px; // 입력창과 높이 맞춤
    }
  }

  .grid-area {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 0;

    // 그리드 내부 스타일 조정
    ::v-deep .real {
      width: 100% !important;

      .rg-container {
        width: 100% !important;
      }

      .rg-check-bar {
        width: 50px !important; // 체크박스 컬럼 너비 조정
      }
    }
  }

  // 페이징 컨테이너 스타일
  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
  }
}
</style>
